/* ado · Ingredient checker (snippets/ado-tool-ingredient-checker.liquid).
   A parent pastes an ingredient list (INCI); we flag adult actives, medicines and common irritants for a 7–17
   year old, in plain words with a source for each flag. Runs entirely in the browser: nothing is sent anywhere.
   Rules follow /blogs/guide/skincare-ingredients-tweens-should-avoid (AAD 2025, AAP 2025, IJDVL, DermNet). */
(() => {
  const SRC = {
    aad: { label: 'American Academy of Dermatology (2025)', url: 'https://www.aad.org/news/dermatologist-guide-skincare' },
    aap: { label: 'American Academy of Pediatrics (2025)', url: 'https://www.healthychildren.org/English/ages-stages/teen/Pages/trendy-skin-care-for-tweens-and-teens-is-it-safe.aspx' },
    aapAcne: { label: 'American Academy of Pediatrics: teen acne', url: 'https://www.healthychildren.org/English/health-issues/conditions/skin/Pages/What-Causes-Acne.aspx' },
    aadAcne: { label: 'American Academy of Dermatology: acne tips', url: 'https://www.aad.org/public/diseases/acne/skin-care/tips' },
    tsdf: { label: 'Indian Journal of Dermatology: steroid-damaged face', url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC4885178/' },
    who: { label: 'World Health Organization: mercury', url: 'https://www.who.int/news-room/fact-sheets/detail/mercury-and-health' },
    dermnet: { label: 'DermNet: fragrance allergy', url: 'https://dermnetnz.org/topics/fragrance-allergy' },
    ijdvl: { label: 'IJDVL: social media and pre-teen skincare', url: 'https://ijdvl.com/the-digital-dermatosis-social-medias-unintended-impact-on-pre-adolescent-skincare/' },
    prism: { label: 'Indian Sunscreen Forum consensus (2025)', url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC12439021/' },
    hq: { label: 'DermNet: hydroquinone', url: 'https://dermnetnz.org/topics/hydroquinone' },
  };
  const GUIDE = {
    avoid: { label: 'Ingredients tweens should avoid', url: '/blogs/guide/skincare-ingredients-tweens-should-avoid' },
    steroid: { label: 'Steroid creams on a child’s face', url: '/blogs/guide/steroid-creams-on-childs-face' },
    pimples: { label: 'Pimples at 13', url: '/blogs/guide/pimples-at-13' },
    niacinamide: { label: 'Can a 12-year-old use niacinamide?', url: '/blogs/guide/niacinamide-for-12-year-old' },
    sun: { label: 'Mineral vs chemical sunscreen for kids', url: '/blogs/guide/mineral-vs-chemical-sunscreen-for-kids' },
  };

  // level: doctor (ask a doctor first) · skip (not needed at this age) · know (worth knowing) · good (gentle basics)
  // lowOk: when the ingredient sits far down the list it is often a small helper (pH adjuster, stabiliser, preservative).
  const RULES = [
    {
      id: 'steroid', level: 'doctor', title: 'Steroid medicine',
      re: /\b(clobetasol|clobetasone|betamethasone|mometasone|fluticasone|hydrocortisone|beclomethasone|halobetasol|triamcinolone|desonide|fluocinolone|fluocinonide|dexamethasone|prednisolone|desoximetasone)\b/,
      why: 'This is a corticosteroid, a medicine, not skincare. Used on the face without a doctor, steroid creams can thin the skin and cause redness, acne-like bumps and dependence (topical steroid-damaged face). Use only as prescribed, for the stated time.',
      src: [SRC.tsdf], guide: GUIDE.steroid,
    },
    {
      id: 'rx-retinoid', level: 'doctor', title: 'Prescription retinoid',
      re: /\b(tretinoin|adapalene|tazarotene|isotretinoin|trifarotene)\b/,
      why: 'A prescription-strength vitamin A medicine used for acne. It can be very drying and irritating and makes skin more sun-sensitive. Only with a dermatologist’s plan.',
      src: [SRC.aad, SRC.aapAcne], guide: GUIDE.pimples,
    },
    {
      id: 'lightening-rx', level: 'doctor', title: 'Skin-lightening medicine or mercury',
      re: /\b(hydroquinone|hydrargyrum|mercur(y|ic|ous)|calomel)\b/,
      why: 'Hydroquinone is a skin-lightening medicine for dark patches; in many countries it is prescription-only, so use it only if a dermatologist prescribes it. Mercury should never be in any skin product: the WHO says mercury-containing skin-lightening products are hazardous and banned in many countries.',
      src: [SRC.hq, SRC.who], guide: GUIDE.steroid,
    },
    {
      id: 'anti-infective', level: 'doctor', title: 'Antibiotic or antifungal medicine',
      re: /\b(clindamycin|gentamicin|neomycin|nadifloxacin|fusidic acid|mupirocin|ketoconazole|miconazole|clotrimazole|luliconazole|terbinafine|ofloxacin|ornidazole|metronidazole)\b/,
      why: 'A medicine for an infection or a specific skin problem. Fine when a doctor prescribes it; not something to use as daily skincare. Combination creams that mix these with a steroid are a common cause of steroid damage on the face in India.',
      src: [SRC.tsdf], guide: GUIDE.steroid,
    },
    {
      id: 'retinol', level: 'skip', title: 'Retinol or another retinoid',
      re: /\b(retinol|retinal|retinaldehyde|retinyl (palmitate|acetate|propionate|retinoate|linoleate)|hydroxypinacolone retinoate)\b/,
      why: 'A form of vitamin A sold for wrinkles and sun damage. Young skin doesn’t need it, and it can cause redness, burning, peeling and sun sensitivity.',
      ages: { '13-17': 'For acne, a dermatologist may choose a retinoid. That’s a treatment plan, not a trend product.' },
      src: [SRC.aad, SRC.aap], guide: GUIDE.avoid,
    },
    {
      id: 'aha', level: 'skip', title: 'Exfoliating acid (AHA or PHA)',
      re: /\b(glycolic acid|lactic acid|mandelic acid|gluconolactone|lactobionic acid)\b/,
      why: 'Acids that peel the top layer of skin. Children’s skin doesn’t need exfoliating, and overuse damages the skin barrier, leaving it red, stinging and sun-sensitive.',
      lowOk: true, src: [SRC.aad, SRC.ijdvl], guide: GUIDE.avoid,
    },
    {
      id: 'bha', level: 'skip', title: 'Salicylic acid (BHA)',
      re: /\b(salicylic acid|betaine salicylate|salix (alba|nigra) (bark|leaf) extract|willow bark extract)\b/,
      why: 'An acid that clears out pores, used for blackheads and acne. For skin without acne it has nothing to do and can dry and irritate.',
      ages: { '13-17': 'If your teen has pimples, a dermatologist may suggest one salicylic acid product. Start with one, not a stack.', '10-12': 'If blackheads or pimples are building up, ask a dermatologist or paediatrician before adding it.' },
      lowOk: true, src: [SRC.aad, SRC.aadAcne], guide: GUIDE.pimples,
    },
    {
      id: 'vitc', level: 'skip', title: 'Vitamin C',
      re: /\b(ascorbic acid|ethyl ascorbic acid|ascorbyl glucoside|sodium ascorbyl phosphate|magnesium ascorbyl phosphate|tetrahexyldecyl ascorbate|ascorbyl tetraisopalmitate|ascorbyl palmitate)\b/,
      why: 'Sold for sun damage and uneven tone, concerns children don’t need to correct. Vitamin C serums are on the AAD’s list of ingredients for tweens and teens to skip, and they can sting.',
      lowOk: true, src: [SRC.aad], guide: GUIDE.avoid,
    },
    {
      id: 'peptide', level: 'skip', title: 'Anti-ageing peptide',
      re: /\b(palmitoyl (tri|tetra|penta|hexa|oligo)?peptide|acetyl (hexa|octa|tetra)peptide|copper tripeptide|matrixyl|argireline|[a-z]*peptide-\d+)\b/,
      why: 'Aimed at fine lines and firmness. There are no fine lines to fix at this age, so it adds cost and ingredients without a benefit.',
      src: [SRC.aad], guide: GUIDE.avoid,
    },
    {
      id: 'spot-fading', level: 'skip', title: 'Spot-fading active',
      re: /\b(kojic acid|alpha[- ]arbutin|arbutin|tranexamic acid|glutathione)\b/,
      why: 'Sold to fade dark spots or tan. A child’s tan fades by itself with sun protection, and there is nothing to “correct” about darker skin. Not needed at this age.',
      src: [SRC.aad], guide: GUIDE.avoid,
    },
    {
      id: 'scrub', level: 'skip', title: 'Physical scrub',
      re: /\b(walnut shell|juglans regia (shell|seed) powder|apricot (seed|kernel) powder|prunus armeniaca (seed|kernel) powder|pumice)\b/,
      why: 'Gritty particles that rub the skin. The AAP advises against physical scrubs for tweens and teens because they irritate and can damage the skin barrier.',
      src: [SRC.aap], guide: GUIDE.avoid,
    },
    {
      id: 'acne-otc', level: 'know', title: 'Acne treatment',
      re: /\b(benzoyl peroxide|azelaic acid|sulfur|sulphur)\b/,
      why: 'An acne treatment, not an everyday ingredient. It can dry the skin (and benzoyl peroxide bleaches towels and pillowcases).',
      ages: { '7-9': 'Children this age rarely need it. If there are pimples, ask your paediatrician first.', '10-12': 'For first pimples, a doctor may suggest it. Not needed for skin without acne.', '13-17': 'The AAP lists benzoyl peroxide among common acne treatments. One product, used as directed, is plenty.' },
      src: [SRC.aapAcne, SRC.aadAcne], guide: GUIDE.pimples,
    },
    {
      id: 'niacinamide', level: 'know', title: 'Niacinamide',
      re: /\b(niacinamide|nicotinamide)\b/,
      why: 'A form of vitamin B3 found in many everyday products. A child doesn’t need a separate niacinamide serum; our guide explains strengths and when it makes sense for a teen.',
      src: [], guide: GUIDE.niacinamide,
    },
    {
      id: 'fragrance', level: 'know', title: 'Fragrance',
      re: /\b(fragrance|parfum|perfume|aroma|limonene|linalool|citronellol|geraniol|citral|eugenol|isoeugenol|coumarin|cinnamal|hexyl cinnamal|amyl cinnamal|benzyl salicylate|benzyl cinnamate|farnesol|hydroxycitronellal|alpha-isomethyl ionone|butylphenyl methylpropional)\b/,
      why: 'Fragrance is a common cause of skin allergy (allergic contact dermatitis). Most children are fine with it, but if your child has eczema, very sensitive skin or reacts to fragrance, choose fragrance-free and patch-test new products.',
      src: [SRC.dermnet, SRC.aap], guide: GUIDE.avoid,
    },
    {
      id: 'essential-oil', level: 'know', title: 'Essential oil',
      re: /\b(melaleuca|tea tree|lavandula|lavender|mentha|peppermint|eucalyptus|citrus|bergamot|rosmarinus|rosemary|eugenia caryophyllus|clove|cinnamomum|pelargonium|geranium|cymbopogon|lemongrass|santalum|cananga|ylang|origanum|thymus)\b.*\boil\b/,
      why: 'Essential oils are concentrated plant fragrances. “Natural” doesn’t mean gentle: DermNet notes that plant extracts and essential oils can cause fragrance allergy too.',
      src: [SRC.dermnet], guide: GUIDE.avoid,
    },
    {
      id: 'chemical-filter', level: 'know', title: 'Chemical (organic) UV filter',
      re: /\b(avobenzone|butyl methoxydibenzoylmethane|octocrylene|oxybenzone|benzophenone-3|homosalate|octinoxate|ethylhexyl methoxycinnamate|octisalate|ethylhexyl salicylate|bemotrizinol|bis-ethylhexyloxyphenol methoxyphenyl triazine|ethylhexyl triazone|diethylamino hydroxybenzoyl hexyl benzoate|drometrizole trisiloxane|terephthalylidene dicamphor sulfonic acid|methylene bis-benzotriazolyl tetramethylbutylphenol|phenylbenzimidazole sulfonic acid|polysilicone-15)\b/,
      why: 'A chemical sunscreen filter. These are approved sun protection, not a problem in themselves. India’s sunscreen consensus says mineral filters (zinc oxide, titanium dioxide) are preferred for children, so this is worth knowing when you compare.',
      src: [SRC.prism], guide: GUIDE.sun,
    },
    {
      id: 'mineral-filter', level: 'good', title: 'Mineral UV filter',
      re: /\b(zinc oxide|titanium dioxide)\b/,
      why: 'Mineral sunscreen filters. India’s sunscreen consensus says these are preferred for children’s skin.',
      src: [SRC.prism], guide: GUIDE.sun,
    },
    {
      id: 'basics', level: 'good', title: 'Gentle moisturising ingredient',
      re: /\b(glycerin|glycerine|ceramide [a-z0-9]+|ceramides|squalane|panthenol|avena sativa|colloidal oat|oat kernel|allantoin|hyaluronic acid|sodium hyaluronate|shea butter|butyrospermum parkii)\b/,
      why: 'Everyday ingredients that help skin hold on to water or feel soft.',
      src: [SRC.aap], guide: null,
    },
  ];
  // Drying alcohol only matters near the top of the list. Fatty alcohols (cetyl, cetearyl, stearyl, behenyl) are gentle.
  const ALCOHOL = { id: 'alcohol', level: 'know', title: 'Drying alcohol near the top',
    why: 'Alcohol high in the list can dry and sting young skin; the AAD advises against astringents and toners for acne-prone skin. Fatty alcohols such as cetyl or cetearyl alcohol are a different kind of ingredient, used to soften and thicken, and aren’t flagged.',
    src: [SRC.aadAcne], guide: GUIDE.avoid };
  const ALCOHOL_RE = /^(alcohol( denat\.?| denatured)?|ethanol|sd alcohol[\s\d-]*[a-z]?|isopropyl alcohol|ethyl alcohol)$/;

  const EXAMPLE = 'Aqua, Glycolic Acid, Alcohol Denat., Niacinamide, Glycerin, Salicylic Acid, Retinol, Ascorbic Acid, Palmitoyl Tripeptide-1, Lavandula Angustifolia (Lavender) Oil, Parfum, Linalool, Citric Acid, Phenoxyethanol';

  const LEVELS = [
    { key: 'doctor', heading: 'Ask a doctor first', lead: 'Medicines to use only on a doctor’s advice, and mercury, which should never be in a skin product.' },
    { key: 'skip', heading: 'Not needed at this age', lead: 'Adult actives aimed at problems children don’t have.' },
    { key: 'know', heading: 'Worth knowing', lead: 'Usually fine, but good to know about.' },
    { key: 'good', heading: 'Gentle basics', lead: 'Ingredients doctors are comfortable with for growing skin.' },
  ];

  const splitList = (text) => text
    .replace(/\r/g, '')
    .replace(/\b(ingredients?|inci|(in)?active( ingredients?)?|other ingredients?|contains)\s*:/gi, ',')
    .replace(/\.\s+(?=[A-Z])/g, ',')
    .split(/[,;\n•·]+|\s\|\s/)
    .map((s) => s.replace(/\s+/g, ' ').replace(/\*+/g, '').trim().replace(/\.$/, '').trim())
    .filter((s) => s.length > 1 && s.length < 140);

  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function analyse(text, age) {
    const items = splitList(text);
    const n = items.length;
    const hits = new Map();
    items.forEach((raw, i) => {
      const low = raw.toLowerCase();
      const pos = i + 1;
      const add = (rule) => {
        if (!hits.has(rule.id)) hits.set(rule.id, { rule, found: [] });
        hits.get(rule.id).found.push({ name: raw, pos });
      };
      RULES.forEach((rule) => { if (rule.re.test(low)) add(rule); });
      if (pos <= 5 && ALCOHOL_RE.test(low)) add(ALCOHOL);
    });
    const groups = {};
    hits.forEach(({ rule, found }) => {
      const low = rule.lowOk && n >= 8 && found.every((f) => f.pos > Math.max(8, Math.ceil(n * 0.6)));
      (groups[rule.level] = groups[rule.level] || []).push({ rule, found, low, ageNote: rule.ages && rule.ages[age] });
    });
    return { n, groups };
  }

  function render(root, result) {
    const out = root.querySelector('[data-ic-results]');
    if (result.n === 0) {
      out.innerHTML = '<p class="ado-tool__empty">Paste an ingredient list first. You’ll find it on the back of the pack or on the product’s page, usually starting with “Aqua” or “Water”.</p>';
      out.focus();
      return;
    }
    const flagged = ['doctor', 'skip', 'know'].reduce((t, k) => t + ((result.groups[k] || []).length), 0);
    let html = `<p class="ado-tool__summary" role="status"><strong>${result.n} ingredients read.</strong> ${flagged === 0 ? 'Nothing on our list was flagged.' : `${flagged} thing${flagged === 1 ? '' : 's'} to look at below.`}</p>`;
    LEVELS.forEach((lv) => {
      const list = result.groups[lv.key];
      if (!list || !list.length) return;
      html += `<section class="ado-tool__group ado-tool__group--${lv.key}"><h3 class="ado-tool__group-title">${esc(lv.heading)}</h3><p class="ado-tool__group-lead">${esc(lv.lead)}</p><ul class="ado-tool__cards" role="list">`;
      list.forEach(({ rule, found, low, ageNote }) => {
        const names = found.map((f) => `${esc(f.name)} <span class="ado-tool__pos">(no. ${f.pos})</span>`).join(', ');
        const srcs = (rule.src || []).map((s) => `<a href="${s.url}" rel="noopener" target="_blank">${esc(s.label)}</a>`).join(' · ');
        html += `<li class="ado-tool__card"><p class="ado-tool__card-title">${esc(rule.title)}</p>
          <p class="ado-tool__found">Found: ${names}</p>
          <p>${esc(rule.why)}</p>
          ${low ? '<p class="ado-tool__aside">It’s far down the list, so there is probably only a little of it, for example as a pH adjuster or stabiliser.</p>' : ''}
          ${ageNote ? `<p class="ado-tool__aside">${esc(ageNote)}</p>` : ''}
          ${lv.key === 'good' || (!srcs && !rule.guide) ? '' : `<p class="ado-tool__src">${srcs ? `Source: ${srcs}` : ''}${srcs && rule.guide ? ' · ' : ''}${rule.guide ? `Read: <a href="${rule.guide.url}">${esc(rule.guide.label)}</a>` : ''}</p>`}
        </li>`;
      });
      html += '</ul></section>';
    });
    if (flagged === 0) {
      html += '<p class="ado-tool__aside">Our list covers the common adult actives, medicines and irritants, not every ingredient that exists. Patch-test anything new on a small area first.</p>';
    }
    out.innerHTML = html;
    out.focus();
  }

  function init(root) {
    const form = root.querySelector('form');
    const field = root.querySelector('textarea');
    root.querySelector('[data-ic-example]')?.addEventListener('click', () => {
      field.value = EXAMPLE;
      form.requestSubmit();
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const age = (form.querySelector('input[name="ic-age"]:checked') || {}).value || '10-12';
      render(root, analyse(field.value, age));
    });
  }

  document.querySelectorAll('[data-ado-ingredient-checker]').forEach(init);
})();
